import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker, Input, Table, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { F910 } from '../../../services/pw9220Api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface AttendanceTabProps {
  attendanceList: F910[];
  attendanceTotal: number;
  loading: boolean;
  onLoad: (params: {
    employee_code?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    size?: number;
  }) => void;
}

const LOG_CODE_MAP: Record<string, string> = {
  Q: '上班簽到',
  S: '外出簽退',
  T: '進入簽到',
  V: '下班簽退',
};

const AttendanceTab: React.FC<AttendanceTabProps> = React.memo(({
  attendanceList,
  attendanceTotal,
  loading,
  onLoad,
}) => {
  const [empCode, setEmpCode] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const doSearch = useCallback(() => {
    const params: {
      employee_code?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      size?: number;
    } = { page, size: pageSize };
    if (empCode.trim()) params.employee_code = empCode.trim();
    if (dateRange[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.date_to = dateRange[1].format('YYYY-MM-DD');
    onLoad(params);
  }, [empCode, dateRange, page, onLoad]);

  useEffect(() => {
    doSearch();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      title: '員工代號', dataIndex: 'f910_01', key: 'f910_01', width: 100,
    },
    {
      title: '日期', dataIndex: 'f910_02', key: 'f910_02', width: 120,
      render: (v: string | null) => v ? v.substring(0, 10) : '',
    },
    {
      title: '班別', dataIndex: 'f910_15', key: 'f910_15', width: 80,
    },
    {
      title: '部門', dataIndex: 'f910_31', key: 'f910_31', width: 80,
    },
    {
      title: '上班時間', dataIndex: 'f910_21', key: 'f910_21', width: 100,
    },
    {
      title: '下班時間', dataIndex: 'f910_22', key: 'f910_22', width: 100,
    },
    {
      title: '應上班', dataIndex: 'f910_32', key: 'f910_32', width: 80,
    },
    {
      title: '應下班', dataIndex: 'f910_33', key: 'f910_33', width: 80,
    },
    {
      title: '遲到(分)', dataIndex: 'f910_29', key: 'f910_29', width: 80,
      render: (v: number) => v > 0 ? <span style={{ color: 'red' }}>{v}</span> : v,
    },
    {
      title: '早退(分)', dataIndex: 'f910_30', key: 'f910_30', width: 80,
      render: (v: number) => v > 0 ? <span style={{ color: 'red' }}>{v}</span> : v,
    },
    {
      title: '最後打卡', dataIndex: 'f910_34', key: 'f910_34', width: 80,
      render: (v: string) => LOG_CODE_MAP[v] || v,
    },
  ];

  return (
    <div className="attendance-tab">
      <div className="search-bar">
        <Space wrap>
          <Input
            placeholder="員工代號"
            value={empCode}
            onChange={(e) => setEmpCode(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => { setPage(1); doSearch(); }}
          >
            查詢
          </Button>
        </Space>
      </div>
      <Table
        dataSource={attendanceList}
        columns={columns}
        rowKey={(r) => `${r.f910_01}-${r.f910_02}`}
        loading={loading}
        size="small"
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize,
          total: attendanceTotal,
          onChange: (p) => setPage(p),
          showTotal: (total) => `共 ${total} 筆`,
          showSizeChanger: false,
        }}
      />
    </div>
  );
});

AttendanceTab.displayName = 'AttendanceTab';

export default AttendanceTab;
